/**
 * Лента фокусов (A3): все фокусы таблицы `FOCUS_DEFS` в один ряд с
 * горизонтальной прокруткой. Доступные — кнопки, активный подчёркнут
 * охрой; недоступные — `disabled`, причина в самом тексте кнопки
 * («Работа — нет работы»), а не в отдельной подсказке. При трёх и более
 * сменах за год — подпись про «метания».
 */
import { availableFocus, FOCUS_DEFS, type Focus, type FocusDef, type Life } from '@life/engine';
import { t } from '@life/i18n';

export interface FocusRibbonProps {
  life: Life;
  /** Активный (выбранный на следующий год) фокус; по умолчанию — `life.focus`. */
  focus?: Focus;
  focusChanges: number;
  onFocus: (f: Focus) => void;
}

/** С какого числа смен за год показывается подпись «метания». */
export const RESTLESS_HINT_AT = 3;

type Reason = 'age' | 'noJob' | 'noCareTarget';

/** Почему фокус недоступен: возраст или условие таблицы (работа, подопечный). */
export function focusReason(life: Life, def: FocusDef): Reason {
  if (life.age < def.minAge || life.age > def.maxAge) return 'age';
  return def.id === 'care' ? 'noCareTarget' : 'noJob';
}

export function FocusRibbon({ life, focus, focusChanges, onFocus }: FocusRibbonProps) {
  const available = new Set(availableFocus(life));
  const active = focus ?? life.focus;
  return (
    <div class="focus-block">
      <div class="ribbon" role="group" aria-label={t('ui.life.focus')}>
        {FOCUS_DEFS.map((def) => {
          const name = t(def.labelKey);
          const ok = available.has(def.id);
          const isActive = def.id === active;
          return (
            <button
              key={def.id}
              type="button"
              class={isActive ? 'btn ghost active' : 'btn ghost'}
              disabled={!ok}
              aria-pressed={isActive}
              onClick={ok ? () => onFocus(def.id) : undefined}
            >
              {ok ? name : t('ui.focus.unavailable', { name, reason: t(`focus.reason.${focusReason(life, def)}`) })}
            </button>
          );
        })}
      </div>
      {focusChanges >= RESTLESS_HINT_AT && <p class="hint restless">{t('ui.focus.restless')}</p>}
    </div>
  );
}
